import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Wallet, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export const AdminLoans = () => {
  const { adminApi } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const res = await adminApi.get('/admin/loans');
      setLoans(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (loanId) => {
    setProcessing(loanId);
    try {
      await adminApi.put(`/admin/loans/${loanId}/approve`);
      toast.success('Loan approval submitted for supervisor review');
      fetchLoans();
    } catch (error) {
      toast.error('Failed to approve loan');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (loanId) => {
    setProcessing(loanId);
    try {
      await adminApi.put(`/admin/loans/${loanId}/reject`);
      toast.success('Loan rejection submitted for supervisor review');
      fetchLoans();
    } catch (error) {
      toast.error('Failed to reject loan');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pendingLoans = loans.filter(l => l.status === 'pending');
  const processedLoans = loans.filter(l => l.status !== 'pending');

  return (
    <div className="space-y-6" data-testid="admin-loans">
      <div>
        <h1 className="font-syne text-2xl font-bold text-gray-900">Loan Applications</h1>
        <p className="text-gray-600">{pendingLoans.length} pending, {processedLoans.length} processed</p>
      </div>

      {/* Pending Loans */}
      {pendingLoans.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-syne text-lg font-bold text-gray-900">Pending Applications</h2>
          {pendingLoans.map((loan) => (
            <Card key={loan.id} className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-xl">R{loan.amount?.toLocaleString()}</p>
                    <p className="text-gray-600 text-sm">{loan.user_name}</p>
                  </div>
                </div>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Pending
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="text-gray-900 capitalize">{loan.loan_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Term</p>
                  <p className="text-gray-900">{loan.term_months} months</p>
                </div>
                <div>
                  <p className="text-gray-500">Interest Rate</p>
                  <p className="text-gray-900">{loan.interest_rate}%</p>
                </div>
                <div>
                  <p className="text-gray-500">Monthly Payment</p>
                  <p className="text-gray-900">R{loan.monthly_payment?.toFixed(2)}</p>
                </div>
              </div>

              {loan.credit_score && (
                <p className="text-gray-500 text-sm mb-4">Credit Score: <span className="text-gray-900 font-medium">{loan.credit_score}</span></p>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => handleApprove(loan.id)}
                  disabled={processing === loan.id}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`approve-loan-${loan.id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {processing === loan.id ? 'Processing...' : 'Approve'}
                </Button>
                <Button
                  onClick={() => handleReject(loan.id)}
                  disabled={processing === loan.id}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid={`reject-loan-${loan.id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Processed Loans */}
      {processedLoans.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-syne text-lg font-bold text-gray-900">Processed Loans</h2>
          <Card className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Applicant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Term</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {processedLoans.map((loan) => (
                    <tr key={loan.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{loan.user_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 capitalize">{loan.loan_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">R{loan.amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{loan.term_months}m</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{loan.interest_rate}%</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          loan.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {loan.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {loans.length === 0 && (
        <Card className="bg-white border border-gray-200 p-12 text-center rounded-xl">
          <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No loan applications yet</p>
        </Card>
      )}
    </div>
  );
};
